import { useEffect, useState, useCallback } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import StoryCard from "@/components/StoryCard";
import { Loader2, BookmarkX } from "lucide-react";
import { toast } from "sonner";

export default function BookmarksPage() {
  const { user, loading: authLoading } = useAuth();
  const nav = useNavigate();
  const [stories, setStories] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/bookmarks");
      setStories(r.data);
    } catch { toast.error("Failed to load bookmarks"); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { if (user) load(); }, [user, load]);

  if (authLoading) return null;
  if (!user) return <Navigate to="/login" replace />;

  const toggleBookmark = async (storyId) => {
    try {
      await api.delete(`/bookmarks/${storyId}`);
      setStories((s) => s.filter((x) => x.id !== storyId));
      toast.success("Removed");
    } catch { toast.error("Action failed"); }
  };

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
      <div className="border-b-2 border-stone-900 pb-6 mb-8">
        <div className="font-mono-head text-xs uppercase tracking-[0.3em] text-stone-500 mb-2">
          // Your private vault
        </div>
        <h1 className="font-serif-head text-4xl sm:text-5xl text-stone-900">Bookmarks</h1>
        <p className="font-mono-head text-xs uppercase tracking-widest text-stone-500 mt-2">
          {stories.length} dossier{stories.length !== 1 ? "s" : ""} saved
        </p>
      </div>

      {loading ? (
        <div className="py-20 text-center font-mono-head text-xs uppercase tracking-widest text-stone-500 flex items-center justify-center gap-2">
          <Loader2 className="w-4 h-4 animate-spin" /> Loading…
        </div>
      ) : stories.length === 0 ? (
        <div className="py-20 text-center" data-testid="empty-bookmarks">
          <BookmarkX className="w-10 h-10 text-stone-300 mx-auto mb-4" />
          <p className="font-mono-head text-sm uppercase tracking-widest text-stone-500 mb-4">
            No bookmarks yet
          </p>
          <button
            onClick={() => nav("/")}
            className="font-mono-head text-xs uppercase tracking-widest bg-stone-900 text-stone-50 px-5 py-3 hover:bg-[#E60000] transition-colors"
          >
            Browse the feed
          </button>
        </div>
      ) : (
        stories.map((s, i) => (
          <StoryCard
            key={s.id}
            story={s}
            index={i}
            isBookmarked={true}
            onToggleBookmark={toggleBookmark}
          />
        ))
      )}
    </div>
  );
}
